import {ActionIcon, Button, Menu, createStyles} from '@mantine/core';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import createChatForEntry from 'llm/data-capture/createChatForEntry';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface StartChatWithEntryButtonProps {
  entryId: string;
  showEditContents: boolean;
  isEditable: boolean;
  showAnalyzeData: boolean;
}

enum EntryAssistantCta {
  AnalyzeData = 'analyze_data',
  AskQuestions = 'ask_questions',
  EditContents = 'edit_contents',
}

enum EntryAssistantCtaRenderMode {
  SingleButton = 'single_button',
  SplitButtonDropdown = 'split_button_dropdown',
  SplitButtonPrimary = 'split_button_primary',
}

type AssistantScope = 'read' | 'write';

interface EntryAssistantCtaAnalyticsProperties {
  agentType: AgentType;
  assistantScope: AssistantScope;
  cta: EntryAssistantCta;
  entryId: string;
  isEditable: boolean;
  renderMode: EntryAssistantCtaRenderMode;
}

const useStyles = createStyles((theme) => ({
  mainButton: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: 'none',
  },
  chevronButton: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: `1px solid ${theme.colors.blue[3]}`,
  },
}));

function getComposeLabel(isEditable: boolean): string {
  return isEditable ? 'Edit contents' : 'Ask questions';
}

function getComposeCta(isEditable: boolean): EntryAssistantCta {
  return isEditable ? EntryAssistantCta.EditContents : EntryAssistantCta.AskQuestions;
}

function getComposeAgentType(isEditable: boolean): AgentType {
  if (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT) {
    return AgentType.ASSISTANT;
  }
  const canAsk = BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
  return isEditable || !canAsk ? AgentType.DATA_CAPTURE : AgentType.ASK;
}

export default function StartChatWithEntryButton({
  entryId,
  showEditContents,
  isEditable,
  showAnalyzeData,
}: StartChatWithEntryButtonProps): JSX.Element {
  const {classes} = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  // Before agent approvals, entry write access was sufficient to edit contents via chat. Once
  // approvals are released to the tenant, editing also requires approvals to be enabled for this
  // user, since they would otherwise have no way to request the access needed to approve edits.
  const canEditContents =
    isEditable &&
    (!BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED || BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS);
  const composeAgentType = getComposeAgentType(canEditContents);

  const getAnalyticsProperties = (
    cta: EntryAssistantCta,
    agentType: AgentType,
    assistantScope: AssistantScope,
    renderMode: EntryAssistantCtaRenderMode
  ): EntryAssistantCtaAnalyticsProperties => ({
    agentType,
    assistantScope,
    cta,
    entryId,
    isEditable,
    renderMode,
  });

  const trackClickedCta = (properties: EntryAssistantCtaAnalyticsProperties, chatSessionId: string): void => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_ENTRY_ASSISTANT_CTA, {
      ...properties,
      chatSessionId,
    });
  };

  const startComposeChat = async (renderMode: EntryAssistantCtaRenderMode): Promise<void> => {
    const properties = getAnalyticsProperties(
      getComposeCta(canEditContents),
      composeAgentType,
      canEditContents ? 'write' : 'read',
      renderMode
    );
    setIsLoading(true);
    try {
      const {chatSessionId} = canEditContents
        ? await createChatForEntry(entryId, composeAgentType)
        : await createChatForEntry(entryId, composeAgentType, {assistantScope: 'read'});
      trackClickedCta(properties, chatSessionId);
    } finally {
      setIsLoading(false);
    }
  };

  const startAnalysisChat = async (renderMode: EntryAssistantCtaRenderMode): Promise<void> => {
    const properties = getAnalyticsProperties(
      EntryAssistantCta.AnalyzeData,
      AgentType.ASSISTANT,
      'read',
      renderMode
    );
    const {chatSessionId} = await createChatForEntry(entryId, AgentType.ASSISTANT, {
      assistantScope: 'read',
    });
    trackClickedCta(properties, chatSessionId);
  };

  // Single option: compose only
  if (showEditContents && !showAnalyzeData) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="sparkle"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={() => startComposeChat(EntryAssistantCtaRenderMode.SingleButton)}
        size={ButtonSize.MINI}
        text={getComposeLabel(canEditContents)}
      />
    );
  }

  // Single option: analysis only
  if (showAnalyzeData && !showEditContents) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="lightbulb"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={() => startAnalysisChat(EntryAssistantCtaRenderMode.SingleButton)}
        size={ButtonSize.MINI}
        text="Analyze data"
      />
    );
  }

  // Both options, editable: split button
  if (canEditContents) {
    return (
      <Button.Group>
        <Button
          className={classes.mainButton}
          color="blue"
          leftIcon={<BenchlingIcon icon="sparkle" />}
          loading={isLoading}
          onClick={() => startComposeChat(EntryAssistantCtaRenderMode.SplitButtonPrimary)}
          size="xs"
          variant="secondary"
        >
          Edit contents
        </Button>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon
              aria-label="More actions"
              className={classes.chevronButton}
              color="blue"
              disabled={isLoading}
              size="xs"
              variant="secondary"
            >
              <BenchlingIcon icon="chevron-down" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => startAnalysisChat(EntryAssistantCtaRenderMode.SplitButtonDropdown)}>
              Analyze data
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    );
  }

  // Both options, cannot edit contents: behavior depends on assistant agent availability
  if (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT) {
    return (
      <AsyncButton
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="lightbulb"
        loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
        onClick={() => startAnalysisChat(EntryAssistantCtaRenderMode.SingleButton)}
        size={ButtonSize.MINI}
        text="Analyze data"
      />
    );
  }

  return (
    <AsyncButton
      buttonStyle={ButtonStyle.TERTIARY_BLUE}
      icon="sparkle"
      loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
      onClick={() => startComposeChat(EntryAssistantCtaRenderMode.SingleButton)}
      size={ButtonSize.MINI}
      text="Ask questions"
    />
  );
}
